import { isApiError } from '@shigo-ui/api-client';
import { z } from 'zod';
import { createShigoApiClient, type ShigoApiConfig } from '../../lib/shigo-api-client';
import { createCampaignsClient } from '../campaigns/campaigns/api';
import { createCreativesClient } from '../campaigns/creatives/api';
import {
  brandSchema,
  brandsPageSchema,
  createBrandMultipartInit,
  serializeBrandsFilter,
  type BrandPayload,
  type BrandsFilter,
} from '../campaigns/brands/model';

export enum BrandCreateOutcome {
  CreatedAndAssigned = 'CREATED_AND_ASSIGNED',
  AssignmentIncomplete = 'ASSIGNMENT_INCOMPLETE',
  OutcomeUnknown = 'OUTCOME_UNKNOWN',
}
const outcomeSchema = z.object({
  outcome: z.enum(BrandCreateOutcome),
  brand: brandSchema.optional(),
  message: z.string().optional(),
  reference: z.string().optional(),
});
export class BrandCreationUncertainError extends Error {}
export function createDemandClient({
  config,
  accessToken,
  agencyId,
  fetcher,
}: {
  config: ShigoApiConfig;
  accessToken: string;
  agencyId?: string;
  fetcher?: typeof fetch;
}) {
  const path = agencyId ? `/api/agency/${encodeURIComponent(agencyId)}` : '/api/admin/demand';
  const options = {
    config: { ...config, apiBaseUrl: config.apiBaseUrl.replace(/\/$/, '') + path },
    accessToken,
    ...(fetcher ? { fetcher } : {}),
    demand: true,
  };
  const api = createShigoApiClient(options.config, accessToken, fetcher);
  return {
    campaigns: createCampaignsClient(options),
    creatives: createCreativesClient(options),
    brands: {
      list: (filter: BrandsFilter) =>
        api.request({ path: `/brands?${serializeBrandsFilter(filter)}`, schema: brandsPageSchema }),
      get: (id: string) =>
        api.request({ path: `/brands/${encodeURIComponent(id)}`, schema: brandSchema }),
      update: (id: string, payload: BrandPayload, logo?: File | null) =>
        api.request({
          path: `/brands/${encodeURIComponent(id)}`,
          schema: brandSchema,
          init: createBrandMultipartInit('PUT', 'updateRequest', payload, logo),
        }),
      create: async (targetAgencyId: string, payload: BrandPayload, logo?: File | null) => {
        try {
          const result = await api.request({
            path: agencyId ? '/brands' : `/agencies/${encodeURIComponent(targetAgencyId)}/brands`,
            schema: outcomeSchema,
            init: createBrandMultipartInit('POST', 'createRequest', payload, logo),
          });
          if (result.outcome !== BrandCreateOutcome.CreatedAndAssigned)
            throw new BrandCreationUncertainError(
              [result.message, result.reference].filter(Boolean).join(' '),
            );
          return result.brand;
        } catch (error) {
          if (isApiError(error)) {
            const outcome = outcomeSchema.safeParse(error.body);
            if (outcome.success && outcome.data.outcome !== BrandCreateOutcome.CreatedAndAssigned)
              throw new BrandCreationUncertainError(
                [outcome.data.message, outcome.data.reference].filter(Boolean).join(' '),
              );
          }
          throw error;
        }
      },
    },
  };
}
